import React, { useCallback, useContext, useEffect, useState } from "react";

import { withRouter } from "react-router-dom";

import { getAPIBase } from "@/core/config";
import * as Sentry from "@sentry/browser";

import Loading from "./Loading";

export const AuthContext = React.createContext();
export const useAuthContext = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [csrfToken, setCsrfToken] = useState(undefined);
  const [state, setState] = useState({});

  const refreshAuth = useCallback(async () => {
    const response = await fetch(`${getAPIBase()}/api/get-user/`, { credentials: "include" });
    const data = await response.json();
    setState(prev => ({
      ...prev,
      user: data.user,
      hubspotToken: data.hubspotToken,
      otp: data.otp
    }));
    setCsrfToken(data.csrfToken);
    return data;
  }, []);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  useEffect(() => {
    if (state.user) {
      console.log(`Setting Sentry user id to ${state.user.id}`);
      Sentry.configureScope(scope => {
        scope.setUser({ id: state.user.id });
      });
    }
    // eslint-disable-next-line
  }, [state.user]);

  useEffect(() => {
    if ("user" in state) {
      // if user is in state then we have heard back from `/api/get-user`

      const hubspotBotScript = document.querySelector("#hs-script-loader");

      if (hubspotBotScript && window.hubspotChatSrc) {
        // window.hubspotChatSrc will be defined on the regenerus domain only

        const onHubspotConversationsReady = () => {
          if (state.hubspotToken && state.user?.email) {
            // user is logged in
            window.hsConversationsSettings = {
              identificationEmail: state.user.email,
              identificationToken: state.hubspotToken
            };
          }
          window.HubSpotConversations.widget.load();
        };

        window.hsConversationsOnReady = [onHubspotConversationsReady];

        if (state.user && state.hubspotToken && window.HubSpotConversations) {
          // i.e. we previously initialised hubspot
          // since only depenendies in this hook are token and user
          // it must be the user has logged in
          window.hsConversationsSettings = {
            identificationEmail: state.user.email,
            identificationToken: state.hubspotToken
          };
          window.HubSpotConversations.resetAndReloadWidget();
        }

        if (!hubspotBotScript.hasAttribute("src")) {
          hubspotBotScript.setAttribute("src", window.hubspotChatSrc);
          hubspotBotScript.setAttribute("async", "");
          hubspotBotScript.setAttribute("defer", "");
        }
      }
    }
  }, [state]);

  // IMPORTANT, don't try and render anything unless we have a CSRF
  // Quite a few pages need to make queries straight-away now, and this safegaurds any issues on that front
  if (csrfToken === undefined || !("user" in state)) {
    return <Loading />;
  }

  return (
    <AuthContext.Provider
      value={{
        user: state.user,
        otp: state.otp,
        setState,
        csrfToken,
        setCsrfToken,
        refreshAuth
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export default withRouter(AuthProvider);
